/**
 * "Open in another tab" (W2-10 projects-data; DESIGN.md §4.1, §5.13; ARCHITECTURE.md §10): only one tab edits a
 * chart at a time (Web Locks, fenced writes), so opening a chart another tab edits asks what to do:
 *  - **Switch to it**: the other tab is asked to come forward (BroadcastChannel; it focuses itself and its title
 *    blinks, since a page cannot switch tabs itself);
 *  - **Edit here instead**: the other tab saves, closes the chart and returns to its Library with a message; this
 *    tab then opens it (waits up to 5 s for the hand-over, then takes the lock).
 * There is no read-only mode in v1.
 */

import { useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import { Button, Dialog, Icon } from '../../ui';
import { useText } from './hooks';
import { m } from './library.messages';
import { editHereInstead, switchToOtherTab } from './libraryActions';
import type { LockedRequest } from './libraryStore';
import s from './Library.module.css';

export interface LockedElsewhereProps {
  readonly services: AppServices;
  readonly request: LockedRequest;
  readonly onClose: () => void;
}

export function LockedElsewhere(props: LockedElsewhereProps) {
  const { services, request, onClose } = props;
  const text = useText(services);
  const [phase, setPhase] = useState<'ask' | 'taking' | 'failed'>('ask');

  const takeOver = async (): Promise<void> => {
    setPhase('taking');
    const outcome = await editHereInstead(services, request);
    if (outcome === 'opened') onClose();
    else setPhase(outcome === 'refused' ? 'failed' : 'ask');
  };

  return (
    <Dialog
      open
      size="sm"
      title={text.t(m.leTitle, { name: request.name })}
      description={text.t(m.leBody)}
      onClose={() => {
        if (phase !== 'taking') onClose();
      }}
      footer={
        <>
          <Button
            variant="ghost"
            disabledReason={phase === 'taking' ? { key: m.leTakingOver } : null}
            onClick={onClose}
          >
            {text.t(m.leCancel)}
          </Button>
          <Button
            icon="ArrowRightLeft"
            disabledReason={phase === 'taking' ? { key: m.leTakingOver } : null}
            onClick={() => {
              void takeOver();
            }}
          >
            {text.t(m.leEditHere)}
          </Button>
          <Button
            variant="primary"
            icon="AppWindow"
            autoFocus
            disabledReason={phase === 'taking' ? { key: m.leTakingOver } : null}
            onClick={() => {
              switchToOtherTab(services, request);
              onClose();
            }}
          >
            {text.t(m.leSwitch)}
          </Button>
        </>
      }
    >
      <ul className={s.choiceList}>
        <li>
          <Icon name="AppWindow" size={16} />
          <span>
            <strong>{text.t(m.leSwitch)}</strong> {text.t(m.leSwitchHelp)}
          </span>
        </li>
        <li>
          <Icon name="ArrowRightLeft" size={16} />
          <span>
            <strong>{text.t(m.leEditHere)}</strong> {text.t(m.leEditHereHelp)}
          </span>
        </li>
      </ul>
      {phase === 'taking' && (
        <p className={s.muted} role="status">
          {text.t(m.leTakingOver)}
        </p>
      )}
      {phase === 'failed' && (
        <p className={s.errorText} role="alert">
          <Icon name="CircleAlert" size={16} /> {text.t(m.leFailed)}
        </p>
      )}
    </Dialog>
  );
}
